import * as clientDb from "./db/clientDb";

export interface HouseholdSummary {
    id: number;
    name: string;
    role: "owner" | "member";
    memberCount: number;
}

export interface HouseholdMember {
    id: number;
    username: string;
    isOwner: boolean;
    joinedAt: string;
}

export interface HouseholdDetail {
    id: number;
    name: string;
    ownerId: number | null;
    inviteCode: string;
    members: HouseholdMember[];
    pendingInvites: { id: number; username: string }[];
    hasGeminiApiKey: boolean;
    geminiApiKeyPreview: string | null;
    geminiModel: string | null;
}

export interface HouseholdInvite {
    household_id: number;
    name: string;
    invited_by: string | null;
    created_at: string;
}

const ACTIVE_KEY = "pantry-active-household";

let activeHouseholdId: number | null = null;
let memberships: HouseholdSummary[] = [];

export function getActiveHouseholdId(): number | null {
    return activeHouseholdId;
}

export function getMemberships(): HouseholdSummary[] {
    return memberships;
}

export function getActiveMembership(): HouseholdSummary | undefined {
    return memberships.find((m) => m.id === activeHouseholdId);
}

async function householdRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
    const res = await fetch(path, {
        ...options,
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
            ...options.headers,
        },
    });
    if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { error?: string }).error || res.statusText);
    }
    if (res.status === 204) return undefined as T;
    return res.json() as Promise<T>;
}

export const householdsApi = {
    list: () => householdRequest<HouseholdSummary[]>("/api/households"),
    get: (id: number) => householdRequest<HouseholdDetail>(`/api/households/${id}`),
    create: (name: string) =>
        householdRequest<HouseholdDetail>("/api/households", {
            method: "POST",
            body: JSON.stringify({ name }),
        }),
    join: (inviteCode: string) =>
        householdRequest<HouseholdDetail>("/api/households/join", {
            method: "POST",
            body: JSON.stringify({ inviteCode }),
        }),
    leave: (id: number, userId: number) =>
        householdRequest<HouseholdDetail>(`/api/households/${id}/members/${userId}`, {
            method: "DELETE",
        }),
    rename: (id: number, name: string) =>
        householdRequest<HouseholdDetail>(`/api/households/${id}`, {
            method: "PATCH",
            body: JSON.stringify({ name }),
        }),
    regenerateInvite: (id: number) =>
        householdRequest<HouseholdDetail>(`/api/households/${id}/invite/regenerate`, {
            method: "POST",
        }),
    addMember: (id: number, username: string) =>
        householdRequest<HouseholdDetail>(`/api/households/${id}/members`, {
            method: "POST",
            body: JSON.stringify({ username }),
        }),
    removeMember: (id: number, userId: number) =>
        householdRequest<HouseholdDetail>(`/api/households/${id}/members/${userId}`, {
            method: "DELETE",
        }),
    transferOwnership: (id: number, userId: number) =>
        householdRequest<HouseholdDetail>(`/api/households/${id}/transfer`, {
            method: "POST",
            body: JSON.stringify({ userId }),
        }),
    listInvites: () => householdRequest<HouseholdInvite[]>("/api/households/invites"),
    acceptInvite: (householdId: number) =>
        householdRequest<HouseholdDetail>(`/api/households/invites/${householdId}/accept`, {
            method: "POST",
        }),
    declineInvite: (householdId: number) =>
        householdRequest<{ ok: boolean }>(`/api/households/invites/${householdId}/decline`, {
            method: "POST",
        }),
    cancelInvite: (householdId: number, userId: number) =>
        householdRequest<HouseholdDetail>(`/api/households/${householdId}/invites/${userId}`, {
            method: "DELETE",
        }),
    updateAiSettings: (
        id: number,
        settings: { geminiApiKey?: string | null; geminiModel?: string | null },
    ) =>
        householdRequest<HouseholdDetail>(`/api/households/${id}`, {
            method: "PATCH",
            body: JSON.stringify(settings),
        }),
};

/** Load memberships, pick the active household, and point clientDb at it. */
export async function initHouseholds(): Promise<void> {
    memberships = await householdsApi.list();

    const saved = Number(localStorage.getItem(ACTIVE_KEY));
    const savedValid = memberships.some((m) => m.id === saved);
    activeHouseholdId = savedValid ? saved : (memberships[0]?.id ?? null);
    if (activeHouseholdId !== null) {
        clientDb.useHousehold(activeHouseholdId);
    }
}

export function setActiveHousehold(id: number): void {
    if (!memberships.some((m) => m.id === id)) return;
    activeHouseholdId = id;
    localStorage.setItem(ACTIVE_KEY, String(id));
    clientDb.useHousehold(id);
}

export async function refreshMemberships(): Promise<void> {
    memberships = await householdsApi.list();
    if (activeHouseholdId !== null && !memberships.some((m) => m.id === activeHouseholdId)) {
        activeHouseholdId = memberships[0]?.id ?? null;
        if (activeHouseholdId !== null) {
            localStorage.setItem(ACTIVE_KEY, String(activeHouseholdId));
            clientDb.useHousehold(activeHouseholdId);
        } else {
            localStorage.removeItem(ACTIVE_KEY);
        }
    }
}
